import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { CheckCircle, X, Plus, GavelIcon, PlusCircle } from "@phosphor-icons/react";

const STATUSES = ["draft", "in_review", "approved", "rejected", "changes_requested"];
const TONES = ["firm", "escalation", "final_notice"];
const LANGS = ["en", "hi", "bn", "ar"];

export default function LegalTemplates() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ jurisdiction: "", status: "" });
  const [open, setOpen] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    const q = new URLSearchParams();
    if (filter.jurisdiction) q.set("jurisdiction", filter.jurisdiction);
    if (filter.status) q.set("status", filter.status);
    api.get(`/legal-templates?${q}`).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const seed = async () => {
    if (!window.confirm("Seed 5 pre-approved templates (IN, BD, SG, AE, UK)?")) return;
    const r = await api.post("/legal-templates/seed");
    alert(`Added ${r.data.added} templates.`);
    load();
  };

  const review = async (tid, decision) => {
    const notes = window.prompt(`Review notes for ${decision}?`) || "";
    await api.post(`/legal-templates/${tid}/review`, { decision, notes, reviewer_kind: "span_lawyer" });
    load();
  };

  return (
    <div className="p-6 space-y-4" data-testid="legal-templates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><GavelIcon size={12}/> Legal templates</div>
          <h1 className="font-display font-bold text-3xl mt-1">C&amp;D Template Library</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            Pre-vetted templates per jurisdiction · used by AI personalization. Only <b>approved</b> templates can be used in case C&amp;D letters.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="filter-jurisdiction" className="text-[12px] hairline px-2 py-1 surface"
                  value={filter.jurisdiction} onChange={e => setFilter({...filter, jurisdiction: e.target.value})}>
            <option value="">All jurisdictions</option>
            {["IN","BD","SG","AE","GB","US"].map(j => <option key={j} value={j}>{j}</option>)}
          </select>
          <select data-testid="filter-status" className="text-[12px] hairline px-2 py-1 surface"
                  value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})}>
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="seed-templates-btn" onClick={seed} className="text-[11px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]">Seed 5</button>
          <button data-testid="new-template-btn" onClick={() => setShowNew(true)} className="span-btn">
            <Plus size={12}/> New template
          </button>
        </div>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-[var(--span-surface-3)] text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Jurisdiction</th>
              <th className="text-left px-3 py-2">Lang / Tone</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--span-text-2)]">No templates. Click "Seed 5" to bootstrap.</td></tr>}
            {items.map(t => (
              <tr key={t.template_id} className="border-t hairline cursor-pointer hover:bg-[var(--span-surface-3)]"
                  onClick={() => setOpen(t)} data-testid={`template-row-${t.template_id}`}>
                <td className="px-3 py-2 font-bold">{t.name}</td>
                <td className="px-3 py-2">{t.jurisdiction}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{t.language?.toUpperCase()} / {t.tone}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 ${
                    t.status==="approved"?"bg-emerald-50 text-emerald-700":
                    t.status==="in_review"?"bg-amber-50 text-amber-700":
                    t.status==="rejected"?"bg-red-50 text-red-700":
                    "bg-[var(--span-surface-3)]"}`}>{t.status}</span>
                </td>
                <td className="px-3 py-2 text-[var(--span-text-2)]">{fmtDate(t.created_at)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e=>e.stopPropagation()}>
                  {t.status === "in_review" && (
                    <>
                      <button data-testid={`approve-${t.template_id}`} onClick={() => review(t.template_id, "approved")}
                              className="text-[10px] hairline px-2 py-1 hover:bg-emerald-50 mr-1">Approve</button>
                      <button data-testid={`reject-${t.template_id}`} onClick={() => review(t.template_id, "rejected")}
                              className="text-[10px] hairline px-2 py-1 hover:bg-red-50">Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div onClick={e=>e.stopPropagation()} className="bg-white max-w-3xl w-full max-h-[85vh] overflow-auto p-6" data-testid="template-preview">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display font-bold text-lg">{open.name}</div>
                <div className="text-[11px] text-[var(--span-text-2)]">{open.jurisdiction} · {open.language?.toUpperCase()} · {open.tone}</div>
              </div>
              <button onClick={() => setOpen(null)}><X size={18}/></button>
            </div>
            <div className="text-[12px] mb-3 text-[var(--span-text-2)]">
              <b>Statutes cited:</b> {(open.statutes_cited || []).join(" · ")}
            </div>
            <div className="hairline p-4 text-[13px] bg-[var(--span-surface-2)]" dangerouslySetInnerHTML={{__html: open.body_html}}/>
            <div className="text-[10px] text-[var(--span-text-2)] mt-3">
              Variables: {(open.body_html.match(/{{([^}]+)}}/g) || []).join(", ") || "(none)"}
            </div>
          </div>
        </div>
      )}

      {showNew && <NewTemplateModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
    </div>
  );
}

function NewTemplateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", jurisdiction: "IN", language: "en", tone: "firm",
                                      body_html: "<p>Draft template body…</p>", statutes_cited_str: "" });
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      statutes_cited: form.statutes_cited_str.split(",").map(s => s.trim()).filter(Boolean),
    };
    delete payload.statutes_cited_str;
    await api.post("/legal-templates", payload);
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="bg-white max-w-xl w-full p-6 space-y-3" data-testid="new-template-modal">
        <div className="font-display font-bold text-lg">New legal template</div>
        <input data-testid="new-tpl-name" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Template name" className="w-full hairline px-2 py-1.5 text-[12px]"/>
        <div className="grid grid-cols-3 gap-2 text-[12px]">
          <input data-testid="new-tpl-jx" required value={form.jurisdiction} onChange={e=>setForm({...form, jurisdiction:e.target.value.toUpperCase()})} placeholder="IN" className="hairline px-2 py-1.5"/>
          <select data-testid="new-tpl-lang" value={form.language} onChange={e=>setForm({...form, language:e.target.value})} className="hairline px-2 py-1.5">{LANGS.map(l=><option key={l} value={l}>{l.toUpperCase()}</option>)}</select>
          <select data-testid="new-tpl-tone" value={form.tone} onChange={e=>setForm({...form, tone:e.target.value})} className="hairline px-2 py-1.5">{TONES.map(t=><option key={t} value={t}>{t}</option>)}</select>
        </div>
        <input data-testid="new-tpl-statutes" value={form.statutes_cited_str} onChange={e=>setForm({...form, statutes_cited_str:e.target.value})} placeholder="Statutes (comma-separated)" className="w-full hairline px-2 py-1.5 text-[12px]"/>
        <textarea data-testid="new-tpl-body" required rows={8} value={form.body_html} onChange={e=>setForm({...form, body_html:e.target.value})} className="w-full hairline px-2 py-1.5 text-[11px] font-mono"/>
        <div className="text-[10px] text-[var(--span-text-2)]">Use placeholders like {`{{client_name}}, {{brand}}, {{recipient_name}}, {{infringement_location}}, {{counsel_name}}`}</div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="text-[11px] hairline px-3 py-1.5">Cancel</button>
          <button data-testid="new-tpl-save" type="submit" className="span-btn">Create as draft</button>
        </div>
      </form>
    </div>
  );
}
